import type { AuthUser } from "./auth";
import { useAuth } from "./auth";
/* Permisos del frontend. Son el espejo de lo que valida el backend
   (la seguridad real está en el servidor; esto solo decide qué se muestra). */

export function esMaestro(u?: AuthUser | null): boolean {
  return !!u && (u.superAdmin === true || u.role === "admin");
}
export function esRecepcion(u?: AuthUser | null): boolean {
  return u?.role === "tienda_recepcion";
}
export function esCocina(u?: AuthUser | null): boolean {
  return u?.role === "cocina";
}
export function esOperativo(u?: AuthUser | null): boolean {
  return u?.role === "operativo";
}
/**
 * "Operar el negocio": cambiar estados, editar/cancelar reservas,
 * marcar llegadas en eventos, editar eventos privados, ver QR.
 * SOLO: admin, gerencia, tienda_supervisor, tienda_recepcion.
 */
export function puedeOperarNegocio(u?: AuthUser | null): boolean {
  const roles = ['admin', 'gerencia', 'tienda_supervisor', 'tienda_recepcion'];
  return !!u && (u.superAdmin === true || roles.includes(u.role ?? ''));
}
export function esTienda(u?: AuthUser | null): boolean {
  const TIENDA_ROLES = ["gerencia", "tienda_supervisor", "tienda_recepcion", "cocina", "operativo"];
  return TIENDA_ROLES.includes(u?.role ?? "");
}
// Reportes: todos menos recepción, + cocina y operativo tienen acceso
export function puedeReportes(u?: AuthUser | null): boolean {
  return !!u && !esRecepcion(u);
}
// Clientes: solo gestión activa (no cocina, no operativo, no recepción)
export function puedeClientes(u?: AuthUser | null): boolean {
  return !!u && !esRecepcion(u) && !esCocina(u) && !esOperativo(u);
}
// Editar eventos (fecha, zona, invitados, checklist config): admin, gerencia, supervisor
export function puedeEditarEventos(u?: AuthUser | null): boolean {
  return !!u && (esMaestro(u) || u?.role === "gerencia" || u?.role === "tienda_supervisor");
}
// Ver/editar el checklist COMPLETO de eventos (todas las tareas, reasignar,
// reordenar): admin, gerencia, supervisor. Cocina y cualquier otro rol solo
// ve/confirma la tarea que tiene asignada (ver EventosPage).
export function puedeEditarChecklist(u?: AuthUser | null): boolean {
  return !!u && (esMaestro(u) || u?.role === "gerencia" || u?.role === "tienda_supervisor");
}
/**
 * Si el correo del usuario coincide con el "organizador" cargado en ESE
 * evento puntual, tiene los mismos derechos de modificación sobre ese
 * evento aunque su rol no sea administrativo/supervisor/gerencia -- espejo
 * del bypass por correo en eventos.service.ts (esPrivilegiadoChecklist).
 */
export function esOrganizadorDeEvento(u?: AuthUser | null, evento?: { organizadorEmail?: string | null } | null): boolean {
  if (!u?.email || !evento?.organizadorEmail) return false;
  return u.email.trim().toLowerCase() === evento.organizadorEmail.trim().toLowerCase();
}
// Ver eventos (lista + detalle): supervisor, admin, recepcion, cocina, operativo (donde asignado)
export function puedeVerEventos(u?: AuthUser | null): boolean {
  return !!u && (esMaestro(u) || esTienda(u));
}
// Configurar sucursal (logo, fondo, colores, zonas): admin, gerencia y supervisor de tienda
export function puedeConfigurarSucursal(u?: AuthUser | null): boolean {
  return !!u && (esMaestro(u) || u?.role === "gerencia" || u?.role === "tienda_supervisor");
}
// Crear/cancelar/cambiar estado de reservas
export function puedeGestionarReservas(u?: AuthUser | null): boolean {
  return !!u && !esCocina(u) && !esOperativo(u);
}
// Solo ver reservas (operativo puede ver + notas, cocina solo ve el calendario)
export function puedeVerReservas(u?: AuthUser | null): boolean {
  return !!u && !esCocina(u);
}
export function esRepartidor(u?: AuthUser | null): boolean {
  return u?.role === "repartidor";
}
// Ver (no gestionar) el QR de encuestas activas: personal con contacto
// directo con el cliente, para poder ofrecérselo a que lo escaneen.
export function puedeVerEncuestaQr(u?: AuthUser | null): boolean {
  return !!u && (puedeConfigurarSucursal(u) || u.role === "cajero" || u.role === "operativo");
}

/** Módulo habilitado para la organización del usuario. Ausente/true = ON, false = OFF. */
export function moduloActivo(u: AuthUser | null | undefined, key: string): boolean {
  return u?.modulos?.[key] !== false;
}

/**
 * Resuelve el acceso efectivo a una ruta/tarjeta puntual: si el usuario
 * tiene un override explícito para esa ruta (asignado por un admin/gerencia/
 * supervisor delegado), ese valor gana sobre lo que el rol daría por
 * defecto -- en cualquiera de los dos sentidos (otorgar de más, o quitar de
 * lo que el rol sí permitiría). Sin override: se respeta el valor base.
 */
export function accesoEfectivo(
  u: AuthUser | null | undefined,
  ruta: string,
  porDefecto: boolean,
): boolean {
  const override = u?.permisosOverride?.[ruta];
  return override !== undefined ? override : porDefecto;
}

/** Delegación: puede gestionar usuarios y sus overrides con el mismo alcance que admin. */
export function puedeGestionarUsuarios(u?: AuthUser | null): boolean {
  return !!u && (esMaestro(u) || u.role === "gerencia" || u.puedeGestionarPermisos === true);
}

// A dónde mandar a cada usuario tras el login / como "inicio".
// Todos aterrizan en /tienda salvo admin/superAdmin (dashboard corporativo)
// y repartidor (su propia vista) -- el acceso a cada sección sigue
// restringido por los RoleRoute/permisos de cada página puntual.
export function rutaInicio(u?: AuthUser | null): string {
  if (esRepartidor(u)) return "/repartidor";
  return esMaestro(u) ? "/" : "/tienda";
}
// Hook para componentes: devuelve la ruta de inicio del usuario actual.
export function useInicio(): string {
  const { user } = useAuth();
  return rutaInicio(user);
}
export const ROLE_LABEL: Record<string, string> = {
  admin: "Administrador",
  gerencia: "Gerencia",
  marketing: "Marketing",
  ventas: "Ventas",
  cuentas_cobrar: "Cuentas por cobrar",
  cuentas_pagar: "Cuentas por pagar",
  cajero: "Cajero",
  tienda_supervisor: "Supervisor de tienda",
  tienda_recepcion: "Recepción / Anfitriona",
  cocina: "Cocina / Chef",
  operativo: "Personal Operativo",
  repartidor: "Repartidor / Delivery",
};
